import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { i18nService } from '../services/i18nService';
import { getTenantId, initializeTenant } from '../services/tenantService';
import ptBR from './locales/pt-BR.json';
import enUS from './locales/en-US.json';
import esES from './locales/es-ES.json';

const loadResources = async (language) => {
  try {
    const tenantId = getTenantId();
    if (!tenantId) {
      console.error('Tenant ID not found when loading translations');
      return false;
    }
    const translations = await i18nService.fetchTranslations(tenantId, language);
    if (translations) {
      i18n.addResourceBundle(language, 'translation', translations, true, true);
      return true;
    }
    return false;
  } catch (error) {
    console.error(`Error loading resources for ${language}:`, error);
    return false;
  }
};

const preloadLanguage = async (language) => {
  if (!i18n.hasResourceBundle(language, 'translation')) {
    await loadResources(language);
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'pt',
    resources: {
      pt: { translation: ptBR },
      en: { translation: enUS },
      es: { translation: esES }
    },
    debug: false,
    interpolation: {
      escapeValue: false,
    },
  });

const initializeTranslations = async () => {
  try {
    // Wait for tenant initialization
    await initializeTenant();
    
    const tenantId = getTenantId();
    if (!tenantId) {
      console.error('Tenant ID not found when initializing translations');
      return;
    }

    const languageConfig = await i18nService.fetchAvailableLanguages(tenantId);
    const defaultLang = languageConfig?.defaultLanguage || 'pt';
    const availableLanguages = languageConfig?.languages?.map(lang => lang.code) || ['pt'];
    
    // Carrega o idioma padrão primeiro
    if (await loadResources(defaultLang)) {
      i18n.changeLanguage(defaultLang);
    }
    
    // Pré-carrega os outros idiomas disponíveis (exceto o padrão que já foi carregado)
    const otherLanguages = availableLanguages.filter(lang => lang !== defaultLang);
    if (otherLanguages.length > 0) {
      await Promise.all(
        otherLanguages.map(language => preloadLanguage(language))
      );
    }

    i18n.on('languageChanged', async (language) => {
      if (!i18n.hasResourceBundle(language, 'translation')) {
        await loadResources(language);
      }
    });

  } catch (error) {
    console.error('Error initializing translations:', error);
    await loadResources('pt');
  }
};

// Initialize translations when the module is imported
initializeTranslations().catch(error => {
  console.error('Failed to initialize translations:', error);
});

i18n.isLanguageLoaded = (language) => {
  return i18n.hasResourceBundle(language, 'translation');
};

export default i18n; 
